import { tokens } from "@blurb/codex-react";
import { createStyles } from "@mantine/core";
import { Flex, IconV3 } from "@rpi/openapi-core";
import React from "react";
import { RpaButtonLink } from "../../../components/RpiLink";
import { Body, Label } from "../../../components/Typography";
import { ACCEPTED_TAX_FILE_TYPE, MAX_TAX_FILE_SIZE_HINT, SUPPORTED_FORMAT_HINT } from "./taxFile";

interface TaxFileDropzoneProps {
  onFiles: (files: File[]) => void;
}

const useStyles = createStyles({
  dropzone: {
    backgroundColor: tokens.color.semantic.bg.primarySubtlest,
    border: `1px dashed ${tokens.color.semantic.border.primary}`,
    borderRadius: tokens.radius.sm,
    padding: `${tokens.spacing[6]} ${tokens.spacing[4]}`,
    width: "100%",
  },
  hiddenInput: {
    display: "none",
  },
  hints: {
    color: tokens.color.semantic.text.subtle,
  },
});

export const TaxFileDropzone: React.FC<TaxFileDropzoneProps> = ({ onFiles }) => {
  const { classes } = useStyles();
  const inputRef = React.useRef<HTMLInputElement>(null);

  const emit = (fileList: FileList | null) => {
    if (fileList && fileList.length) onFiles(Array.from(fileList));
  };

  const openPicker = () => inputRef.current?.click();

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    emit(event.dataTransfer.files);
  };

  return (
    <Flex.Column gap={tokens.spacing[1]} fullWidth>
      <div className={classes.dropzone} onDragOver={(event) => event.preventDefault()} onDrop={handleDrop}>
        <Flex.Column align="center" gap={tokens.spacing[1]} fullWidth>
          <IconV3 icon="upload_file" size={32} color={tokens.color.semantic.icon.primary} />
          <Body size="base">
            <RpaButtonLink data-testid="tax-file-choose" onClick={openPicker}>
              Choose a file
            </RpaButtonLink>{" "}
            or drag &amp; drop it here
          </Body>
        </Flex.Column>
      </div>

      <Flex.Row justify="space-between" fullWidth className={classes.hints}>
        <Label size="sm">{MAX_TAX_FILE_SIZE_HINT}</Label>
        <Label size="sm">{SUPPORTED_FORMAT_HINT}</Label>
      </Flex.Row>

      <input
        ref={inputRef}
        className={classes.hiddenInput}
        type="file"
        accept={ACCEPTED_TAX_FILE_TYPE}
        multiple
        data-testid="tax-file-input"
        onChange={(event) => {
          emit(event.target.files);
          event.target.value = "";
        }}
      />
    </Flex.Column>
  );
};
